import React, { useEffect } from 'react';
import {
  Route, Switch, useLocation, useHistory,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import routes, { unauthenticatedRoutes } from '../../../routes/routes';
import SubNavigation from '../../core/common/sub-navigation';
import Header from '../../core/common/top-nav';
import Toast from '../../core/common/toast-message';
import { AlertContainer, NavigationContainer } from './App.style';
import { NODE_ENV, OIDC_AUTHORITY } from '../../../env';
import { STATUS_TYPES, MODE_TYPES } from '../../../constants';
import { getApplicationMetadata } from '../../../store/metadata/thunkActions';
import { getUserProfileSettings } from '../../../store/profile-settings/thunkActions';
import InactivityModal from '../../core/common/modals/inactivity-modal';
import { setDocumentTitle } from '../../../utils/common';
import { updateQueryString, getFilterStateFromQueryParams } from '../../../utils/mappers';
import { setModeType } from '../../../store/mode/slice';
/**
 * App Container component
 * @return {element} - app container
 */
const App = () => {
  const dispatch = useDispatch();
  const metadata = useSelector((state) => state.metadata);
  const profileSettings = useSelector((state) => state.profileSettings);
  const user = useSelector((state) => state.oidc.user);
  const isAuthenticated = (user && !user.expired) || (NODE_ENV === 'development' && OIDC_AUTHORITY === 'fake');
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const { modeType } = getFilterStateFromQueryParams(searchParams);

  // Update mode type in redux with respective
  // mode query parameter
  useEffect(() => {
    if (!modeType) {
      history.replace(`?${updateQueryString(searchParams, { modeType: MODE_TYPES.truckload })}`);
      dispatch(setModeType(MODE_TYPES.truckload));
    } else if (modeType === MODE_TYPES.lessThanTruckload) {
      dispatch(setModeType(MODE_TYPES.lessThanTruckload));
    }
  }, [dispatch, history, modeType, searchParams]);

  // Listen to changes in the pathname and mode
  // set document title as neccessary
  useEffect(() => {
    const { pathname } = location;
    if (modeType === MODE_TYPES.truckload) {
      setDocumentTitle('Truckload Tracking Queue');
    } else if (modeType === MODE_TYPES.lessThanTruckload) {
      setDocumentTitle('LTL Tracking Queue');
    } else if (pathname === '/profilesettings') {
      setDocumentTitle('T&T Profile Settings');
    } else {
      setDocumentTitle('GlobalTranz Track & Trace');
    }
  }, [location, modeType]);

  useEffect(() => {
    if (isAuthenticated && metadata.status === STATUS_TYPES.UNLOAD_STATE) {
      dispatch(getApplicationMetadata());
    }
  }, [dispatch, isAuthenticated, metadata.status]);

  useEffect(() => {
    if (isAuthenticated && profileSettings?.status === STATUS_TYPES.UNLOAD_STATE) {
      dispatch(getUserProfileSettings());
    }
  }, [dispatch, isAuthenticated, profileSettings.status]);

  const routesToShow = isAuthenticated ? routes : unauthenticatedRoutes;
  const renderedRoutes = routesToShow.map((route) => (
    <Route
      key={route.id}
      component={route.component}
      exact={route.exact}
      path={route.path}
    />
  ));

  return (
    <div className="app-container">
      <NavigationContainer>
        <Header />
        {isAuthenticated && <SubNavigation />}
        <AlertContainer>
          <Toast />
        </AlertContainer>
      </NavigationContainer>
      <Switch>
        {renderedRoutes}
      </Switch>
      {isAuthenticated && <InactivityModal />}
    </div>
  );
};

export default App;
